import { useEffect, useState } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { InterviewProvider, useInterviewState } from "./state/InterviewProvider";
import { SkipLink } from "./components/ui/SkipLink";
import { Tabs } from "./components/ui/Tabs";
import { ModeBadge } from "./components/governance/ModeBadge";
import { RunCheckIn } from "./views/RunCheckIn";
import { DesignYourOwn } from "./views/DesignYourOwn";
import { ChwView } from "./views/ChwView";
import { api, type HealthResponse } from "./api/client";
import { t } from "./i18n/strings";
import { useLocale, useSetLocale } from "./i18n/LocaleContext";
import type { Locale } from "./templates/types";

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const locale = useLocale();
  const setLocale = useSetLocale();
  // §10: "Fallback is never silent." When a live turn fails transiently and
  // Rails answers from DemoProvider, the turn response says so
  // (meta.degraded) and the header badge must stop reading "Live". Without
  // this, a quota or timeout failure showed Demo replies under a Live badge.
  const { state: interview } = useInterviewState();

  useEffect(() => {
    const controller = new AbortController();
    api
      .health(controller.signal)
      .then(setHealth)
      .catch(() => setHealth(null));
    return () => controller.abort();
  }, []);

  const tabItems = [
    { id: "/run", label: t(locale, "tabs.run") },
    { id: "/design", label: t(locale, "tabs.design") },
    { id: "/chw", label: t(locale, "tabs.chw") },
  ];

  const activeTab = tabItems.some((tab) => tab.id === location.pathname) ? location.pathname : "/run";

  return (
    <div className="flex h-screen flex-col bg-paper">
      <SkipLink />
      {/*
        §17/§20 item 10: no horizontal scroll at 375px. This is a
        `flex-col`/`sm:flex-row` stack, not `flex-wrap` — wrap looked like
        the right tool, but with `min-w-0` on the tabs group (needed for its
        own `overflow-x-auto` below to be able to shrink at all), a wrapping
        flex row never actually wraps: the shrinkable item absorbs the
        squeeze down to near-zero width instead of dropping to a new line,
        since `min-w-0` is exactly what tells the browser "yes, shrink this
        past its content size" rather than "wrap because you can't."
        Forcing two full-width rows below `sm` sidesteps that tension
        entirely — the tabs group gets the whole row's width, so its
        internal scroll only ever activates when tabs truly don't fit even
        with the full 375px, not because the mode badge/locale toggle stole
        room from it in a single shared row.
      */}
      <header className="flex flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex min-w-0 items-center gap-4">
          <span className="shrink-0 font-display text-lg text-ink">Fieldnote</span>
          <Tabs items={tabItems} activeId={activeTab} onChange={(id) => navigate(id)} aria-label={t(locale, "tabs.ariaLabel")} />
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <LocaleToggle locale={locale} onChange={setLocale} />
          <ModeBadge health={health} degraded={interview.meta.degraded} locale={locale} />
        </div>
      </header>
      <main id="main-content" className="min-h-0 flex-1 overflow-hidden p-3">
        <Routes>
          <Route path="/" element={<RunCheckIn />} />
          <Route path="/run" element={<RunCheckIn />} />
          <Route path="/design" element={<DesignYourOwn />} />
          <Route path="/chw" element={<ChwView />} />
        </Routes>
      </main>
    </div>
  );
}

/**
 * A simple two-button toggle rather than a <select> — only two locales
 * exist (§15), and both are always visible so there is no hidden state.
 * Dispatches the existing LOCALE_CHANGED reducer action, which keeps all
 * interview data intact (§15: "Switching locale mid-interview keeps all
 * existing data").
 */
function LocaleToggle({ locale, onChange }: { locale: Locale; onChange: (locale: Locale) => void }) {
  return (
    <div
      role="group"
      aria-label={t(locale, "localeToggle.ariaLabel")}
      className="inline-flex rounded-lg border border-border bg-paper-dim p-1"
    >
      {(["en", "es"] as const).map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          aria-pressed={locale === option}
          className={clsx(
            "rounded-md px-2 py-1 text-xs font-medium transition-colors",
            locale === option ? "bg-paper text-ink shadow-sm" : "text-ink-soft hover:text-ink",
          )}
        >
          {t(locale, option === "en" ? "localeToggle.en" : "localeToggle.es")}
        </button>
      ))}
    </div>
  );
}

export function App() {
  return (
    <InterviewProvider>
      <AppShell />
    </InterviewProvider>
  );
}
